/**
 * Centralized timestamp formatting utilities for the application.
 * 
 * This module provides robust handling of various timestamp formats that may come from
 * Firestore, including edge cases like pending serverTimestamp() sentinels and serialized
 * timestamp objects. Using these utilities ensures consistent, error-free timestamp
 * display across the entire application.
 * 
 * SUPPORTED TIMESTAMP TYPES:
 * 1. Firestore Timestamp objects with toDate() method
 * 2. Serialized timestamp objects with seconds/_seconds properties (from snapshots or JSON)
 * 3. Pending serverTimestamp() sentinels (not yet resolved by server)
 * 4. Regular JavaScript Date objects
 * 5. ISO date strings (e.g., "2026-01-16T15:30:00Z")
 * 6. Milliseconds since epoch (numbers)
 * 
 * PREDEFINED FORMAT PRESETS:
 * - 'datetime'     : "16 Jan 2026, 3:45 PM" (default, good for most use cases)
 * - 'datetime-at'  : "16 January 2026 at 3:45 PM" (more verbose, conversational)
 * - 'datetime-us'  : "Jan 16, 2026, 3:45 PM" (US-style month-first)
 * - 'date-short'   : "16 Jan 2026" (date only, abbreviated month)
 * - 'date-long'    : "16 January 2026" (date only, full month name)
 * - 'date-us'      : "Jan 16, 2026" (US-style date only)
 * - 'time'         : "3:45 PM" (time only)
 * - 'iso'          : "2026-01-16" (ISO date format, good for data/exports)
 * 
 * USAGE EXAMPLES:
 * 
 * // Basic usage with default format
 * formatTimestamp(patient.createdAt)  // "16 Jan 2026, 3:45 PM"
 * 
 * // Using a preset
 * formatTimestamp(patient.createdAt, { preset: 'date-short' })  // "16 Jan 2026"
 * 
 * // Custom format string (uses date-fns format tokens)
 * formatTimestamp(patient.createdAt, { format: 'yyyy-MM-dd HH:mm' })  // "2026-01-16 15:45"
 * 
 * // Custom fallback message
 * formatTimestamp(null, { fallback: 'Not set' })  // "Not set"
 * 
 * // Using the convenience functions
 * formatDateShort(patient.dateOfBirth)  // "16 Jan 2026"
 * formatDateTime(patient.lastLogin)     // "16 Jan 2026, 3:45 PM"
 */

import { format } from 'date-fns';

/**
 * Predefined format presets for common timestamp display patterns.
 * These use date-fns format tokens: https://date-fns.org/docs/format
 */
export const TIMESTAMP_PRESETS = {
  // Full datetime formats
  'datetime': 'd MMM yyyy, h:mm a',           // "16 Jan 2026, 3:45 PM"
  'datetime-at': "d MMMM yyyy 'at' h:mm a",   // "16 January 2026 at 3:45 PM"
  'datetime-us': 'MMM d, yyyy, h:mm a',       // "Jan 16, 2026, 3:45 PM"
  
  // Date only formats
  'date-short': 'd MMM yyyy',                  // "16 Jan 2026"
  'date-long': 'd MMMM yyyy',                  // "16 January 2026"
  'date-us': 'MMM d, yyyy',                    // "Jan 16, 2026"
  
  // Time only
  'time': 'h:mm a',                            // "3:45 PM"
  
  // ISO format (useful for data exports, APIs)
  'iso': 'yyyy-MM-dd',                         // "2026-01-16"
  'iso-datetime': "yyyy-MM-dd'T'HH:mm:ss",     // "2026-01-16T15:45:00"
};

/**
 * Default fallback messages for various error states.
 * Can be overridden by passing a custom `fallback` option.
 */
const DEFAULT_FALLBACKS = {
  empty: 'Unknown date',
  pending: 'Saving...',
  invalid: 'Unknown date',
};

/**
 * Attempts to convert various timestamp formats into a JavaScript Date object.
 * Returns null if conversion fails or the timestamp is pending.
 * 
 * @param {any} timestamp - The timestamp to convert
 * @returns {{ date: Date | null, isPending: boolean }} - The converted date and pending status
 */
function parseTimestamp(timestamp) {
  // Handle null/undefined
  if (timestamp === null || timestamp === undefined) {
    return { date: null, isPending: false };
  }

  // Handle plain strings (already formatted dates or ISO strings)
  if (typeof timestamp === 'string') {
    const parsed = new Date(timestamp);
    if (!isNaN(parsed.getTime())) {
      return { date: parsed, isPending: false };
    }
    // If it's a non-parseable string, treat it as already formatted and return null
    // The caller should handle this case
    return { date: null, isPending: false, rawString: timestamp };
  }

  // Handle Firestore Timestamp objects with toDate() method
  if (timestamp.toDate && typeof timestamp.toDate === 'function') {
    try {
      const date = timestamp.toDate();
      if (!isNaN(date.getTime())) {
        return { date, isPending: false };
      }
    } catch {
      // Fall through to other handling
    }
  }

  // Handle pending serverTimestamp() sentinels.
  // These are FieldValue objects that get resolved server-side after the write commits.
  // They may appear as objects with _methodName or other internal properties.
  if (timestamp._methodName || timestamp.type === 'serverTimestamp') {
    return { date: null, isPending: true };
  }

  // Handle serialized Firestore timestamp objects (e.g., from JSON or snapshots).
  // These have seconds/_seconds and nanoseconds/_nanoseconds but no toDate() method.
  const seconds = timestamp.seconds ?? timestamp._seconds;
  if (typeof seconds === 'number') {
    const date = new Date(seconds * 1000);
    if (!isNaN(date.getTime())) {
      return { date, isPending: false };
    }
  }

  // Handle numeric timestamps (milliseconds since epoch)
  if (typeof timestamp === 'number') {
    const date = new Date(timestamp);
    if (!isNaN(date.getTime())) {
      return { date, isPending: false };
    }
  }

  // Handle Date objects
  if (timestamp instanceof Date) {
    if (!isNaN(timestamp.getTime())) {
      return { date: timestamp, isPending: false };
    }
  }

  // Last resort: try to parse as Date
  try {
    const date = new Date(timestamp);
    if (!isNaN(date.getTime())) {
      return { date, isPending: false };
    }
  } catch {
    // Ignore parsing errors
  }

  return { date: null, isPending: false };
}

/**
 * Formats a timestamp for display in the UI.
 * 
 * Handles multiple timestamp formats that may come from Firestore, including edge cases
 * like pending serverTimestamp() sentinels and serialized timestamp objects.
 * 
 * @param {Object|string|number|Date|null} timestamp - The timestamp to format
 * @param {Object} options - Formatting options
 * @param {string} [options.preset='datetime'] - One of the predefined format presets
 * @param {string} [options.format] - Custom date-fns format string (overrides preset)
 * @param {string} [options.fallback] - Custom fallback message for null/undefined timestamps
 * @param {string} [options.pendingMessage] - Custom message for pending serverTimestamp()
 * @param {string} [options.invalidMessage] - Custom message for invalid/unparseable timestamps
 * @returns {string} Formatted date string or fallback message
 * 
 * @example
 * // Default format
 * formatTimestamp(patient.createdAt)
 * // => "16 Jan 2026, 3:45 PM"
 * 
 * @example
 * // Using a preset
 * formatTimestamp(patient.createdAt, { preset: 'date-short' })
 * // => "16 Jan 2026"
 * 
 * @example
 * // Custom format string
 * formatTimestamp(patient.createdAt, { format: 'yyyy-MM-dd' })
 * // => "2026-01-16"
 * 
 * @example
 * // Custom fallback
 * formatTimestamp(null, { fallback: 'Not available' })
 * // => "Not available"
 */
export function formatTimestamp(timestamp, options = {}) {
  const {
    preset = 'datetime',
    format: customFormat,
    fallback = DEFAULT_FALLBACKS.empty,
    pendingMessage = DEFAULT_FALLBACKS.pending,
    invalidMessage = DEFAULT_FALLBACKS.invalid,
  } = options;

  // Handle empty timestamps
  if (timestamp === null || timestamp === undefined || timestamp === '') {
    return fallback;
  }

  try {
    const { date, isPending, rawString } = parseTimestamp(timestamp);

    // If it's a pending serverTimestamp, return the pending message
    if (isPending) {
      return pendingMessage;
    }

    // If parseTimestamp returned a raw string (already formatted), return it
    if (rawString) {
      return rawString;
    }

    // If we couldn't parse the timestamp, return the invalid message
    if (!date) {
      return invalidMessage;
    }

    // Get the format string (custom format takes precedence over preset)
    const formatString = customFormat || TIMESTAMP_PRESETS[preset] || TIMESTAMP_PRESETS.datetime;

    return format(date, formatString);
  } catch (error) {
    // Log the error for debugging but return a safe fallback to prevent render crashes
    console.error('Error formatting timestamp:', error, 'Timestamp value:', timestamp);
    return invalidMessage;
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// Convenience functions for common format presets
// These provide a cleaner API for frequently-used formats
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Formats a timestamp as a full datetime: "16 Jan 2026, 3:45 PM"
 * This is the default format and most commonly used across the app.
 */
export function formatDateTime(timestamp, fallback) {
  return formatTimestamp(timestamp, { preset: 'datetime', fallback });
}

/**
 * Formats a timestamp as a conversational datetime: "16 January 2026 at 3:45 PM"
 * Useful for more verbose, human-readable contexts like activity logs.
 */
export function formatDateTimeVerbose(timestamp, fallback) {
  return formatTimestamp(timestamp, { preset: 'datetime-at', fallback });
}

/**
 * Formats a timestamp as a short date: "16 Jan 2026"
 * Useful for tables and lists where space is limited.
 */
export function formatDateShort(timestamp, fallback) {
  return formatTimestamp(timestamp, { preset: 'date-short', fallback });
}

/**
 * Formats a timestamp as a long date: "16 January 2026"
 * Useful for formal documents and headers.
 */
export function formatDateLong(timestamp, fallback) {
  return formatTimestamp(timestamp, { preset: 'date-long', fallback });
}

/**
 * Formats a timestamp as time only: "3:45 PM"
 * Useful when displaying multiple events on the same day.
 */
export function formatTimeOnly(timestamp, fallback) {
  return formatTimestamp(timestamp, { preset: 'time', fallback });
}

/**
 * Formats a timestamp as an ISO date: "2026-01-16"
 * Useful for data exports, APIs, and filenames.
 */
export function formatDateISO(timestamp, fallback) {
  return formatTimestamp(timestamp, { preset: 'iso', fallback });
}

// ─────────────────────────────────────────────────────────────────────────────
// Utility functions for timestamp validation and comparison
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Checks if a value is a valid timestamp that can be formatted.
 * Useful for conditional rendering of timestamp displays.
 * 
 * @param {any} timestamp - The value to check
 * @returns {boolean} True if the timestamp is valid and can be formatted
 */
export function isValidTimestamp(timestamp) {
  if (timestamp === null || timestamp === undefined || timestamp === '') {
    return false;
  }
  
  const { date, isPending } = parseTimestamp(timestamp);
  return date !== null && !isPending;
}

/**
 * Checks if a timestamp is a pending serverTimestamp() that hasn't resolved yet.
 * Useful for showing loading states or pending indicators.
 * 
 * @param {any} timestamp - The value to check
 * @returns {boolean} True if the timestamp is pending
 */
export function isPendingTimestamp(timestamp) {
  if (timestamp === null || timestamp === undefined) {
    return false;
  }
  
  const { isPending } = parseTimestamp(timestamp);
  return isPending;
}

/**
 * Converts any supported timestamp format to a JavaScript Date object.
 * Returns null if the timestamp is invalid or pending.
 * 
 * Useful when you need to perform date calculations or comparisons.
 * 
 * @param {any} timestamp - The timestamp to convert
 * @returns {Date|null} The Date object or null if invalid
 */
export function toDate(timestamp) {
  const { date } = parseTimestamp(timestamp);
  return date;
}

/**
 * Milliseconds since 1 Jan 1970 for any timestamp the dashboard might be holding.
 * Returns null when the value cannot be read (missing, still saving, or junk).
 *
 * The patient tables only print the calendar day ("8 Oct 2026"). The value
 * underneath still has a clock time: a Firestore Timestamp, a millisecond
 * number from the session cache, an ISO string, or a plain
 * {seconds, nanoseconds} map left behind when a Timestamp is turned into JSON.
 * Callers that sort "Date Added" need this full instant. Comparing the printed
 * day, or running `new Date()` on that plain map (which produces an invalid
 * date), leaves everyone added on the same day in load order, so the newest
 * patient is not first.
 *
 * @param {any} timestamp
 * @returns {number|null}
 */
export function timestampToMillis(timestamp) {
  if (timestamp == null || timestamp === '') return null;

  // A live Firestore Timestamp. toMillis() keeps the time of day, including
  // the milliseconds stored in the nanoseconds field.
  if (typeof timestamp.toMillis === 'function') {
    try {
      const ms = timestamp.toMillis();
      if (Number.isFinite(ms)) return ms;
    } catch {
      // Fall through to the shared parser below.
    }
  }

  const { date } = parseTimestamp(timestamp);
  if (!date) return null;

  let ms = date.getTime();
  if (!Number.isFinite(ms)) return null;

  // parseTimestamp reads a plain {seconds, nanoseconds} map from whole seconds
  // only. Put the leftover milliseconds back so two patients created in the
  // same second still have a stable, time-based order. Skip this when toDate()
  // already folded those milliseconds in.
  const alreadyIncludesSubseconds = typeof timestamp.toDate === 'function'
    || timestamp instanceof Date
    || typeof timestamp === 'number'
    || typeof timestamp === 'string';
  if (!alreadyIncludesSubseconds && typeof timestamp === 'object') {
    const nanos = timestamp.nanoseconds ?? timestamp._nanoseconds;
    if (typeof nanos === 'number' && Number.isFinite(nanos)) {
      ms += Math.floor(nanos / 1e6);
    }
  }

  return ms;
}

/**
 * Sort comparator for a timestamp column.
 *
 * `direction` is 'ascending' (oldest first) or 'descending' (newest first,
 * which is the Date Added default). The comparison uses the full clock time,
 * not the calendar day shown in the cell.
 *
 * A value we cannot read always sorts after a real timestamp, in either
 * direction, so a blank date never jumps above a patient who was just added.
 *
 * @param {any} a
 * @param {any} b
 * @param {'ascending'|'descending'} [direction='ascending']
 * @returns {number} Negative when `a` should come first, positive when `b` should.
 */
export function compareTimestamps(a, b, direction = 'ascending') {
  const aMs = timestampToMillis(a);
  const bMs = timestampToMillis(b);

  if (aMs == null && bMs == null) return 0;
  if (aMs == null) return 1;
  if (bMs == null) return -1;

  const diff = aMs - bMs;
  if (diff === 0) return 0;
  return direction === 'descending' ? -diff : diff;
}
